import React, { useState, useEffect, useRef } from "react";
import Button from "react-bootstrap/Button";
import { TextArea, Checkbox } from "@/components";
import { DefaultProfile } from "@/images";
import { toast } from "react-toastify";
import {
  loggedInUser,
  addDirectMessage,
  upperCaseEachSentenceFirst,
  getUserType,
  isMessageComplianceActive,
  getTenantComplianceConfig,
  COMPLIANCE_SURFACE,
  COMPLIANCE_DEBOUNCE_MS,
} from "@/helpers";
import { USER_TYPE_AGENT } from "@/constants/common";
import { useAppConfig } from "@/appConfig";
import {
  checkMessageCompliance,
  logMessageComplianceFlag,
} from "@/services/v2/messageComplianceService";
import ComplianceWarning from "@/components/Messages/ComplianceWarning";

const SendMessage = ({ wantAd, onSignUpRequired }) => {
  const [messageCharCount, setMessageCharCount] = useState(0);
  const [message, setMessage] = useState("");
  const [msgError, setMsgError] = useState("");
  const userType = getUserType();
  const isAgent = userType === USER_TYPE_AGENT;
  const [termsAccepted, setTermsAccepted] = useState(!isAgent);
  const [isLoading, setIsLoading] = useState(false);
  const appConfig = useAppConfig();
  const complianceActive = isMessageComplianceActive(appConfig, userType);
  const tenantComplianceConfig = getTenantComplianceConfig(appConfig);
  const [complianceResult, setComplianceResult] = useState(null);
  const debounceRef = useRef(null);
  const lastCheckedRef = useRef("");

  const userAvatar = wantAd?.userAvatar;
  const wantCreatedFirstName = wantAd?.userFirstName;
  const wantCreatedUserName =
    wantAd?.userFirstName + " " + wantAd?.userLastName;
  const wantAdCompany = wantAd?.userCompany;
  const wantAdLicenseNo = wantAd?.LicenseNo;
  const wantAdPhone = wantAd?.userPhone;
  const agentSlug = wantAd?.userSlug;

  const userDetails = loggedInUser();

  const isLoggedIn = !!userDetails;

  const shouldDisableButton =
    !termsAccepted ||
    messageCharCount >= 2000 ||
    messageCharCount <= 0 ||
    isLoading;

  // Restore message from sessionStorage (survives page navigation during signup)
  useEffect(() => {
    const key = `pendingMessage_${wantAd?.wantAdId}`;
    const saved = sessionStorage.getItem(key);
    if (saved && !message) {
      try {
        const { message: savedMsg } = JSON.parse(saved);
        if (savedMsg) {
          setMessage(savedMsg);
          setMessageCharCount(savedMsg.length);
        }
        sessionStorage.removeItem(key);
      } catch (e) {
        sessionStorage.removeItem(key);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save message to sessionStorage before triggering signup
  const handleSignUpRequired = () => {
    if (message.trim()) {
      sessionStorage.setItem(
        `pendingMessage_${wantAd?.wantAdId}`,
        JSON.stringify({
          message,
          returnPath: window.location.pathname,
        })
      );
    }
    onSignUpRequired();
  };

  const onChangeText = (event) => {
    let value = event.target.value;

    value = upperCaseEachSentenceFirst(value);

    setMessage(value);
    setMessageCharCount(value.length);

    if (!complianceActive) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setComplianceResult(null);
      lastCheckedRef.current = "";
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (value === lastCheckedRef.current) return;
      lastCheckedRef.current = value;
      try {
        const res = await checkMessageCompliance(value, tenantComplianceConfig);
        if (res?.errorCode === 0) {
          setComplianceResult(res.result || res.data || null);
        }
      } catch (err) {
        console.error("Compliance check failed:", err);
      }
    }, COMPLIANCE_DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSendMessage = async () => {
    setIsLoading(true);
    const senderId = userDetails?.userInfo.userId;
    const receiverId = wantAd?.userId;

    const chatId =
      senderId < receiverId
        ? `${senderId}_${receiverId}`
        : `${receiverId}_${senderId}`;

    let finalCheck = complianceResult;
    if (complianceActive && message !== lastCheckedRef.current) {
      try {
        const res = await checkMessageCompliance(message, tenantComplianceConfig);
        if (res?.errorCode === 0) {
          finalCheck = res.result || res.data || null;
          setComplianceResult(finalCheck);
          lastCheckedRef.current = message;
        }
      } catch (err) {
        console.error("Final compliance check failed:", err);
      }
    }

    if (complianceActive && finalCheck?.violations?.length > 0) {
      logMessageComplianceFlag({
        chatId,
        recipientUserId: receiverId,
        surface: COMPLIANCE_SURFACE.WANT_AD_INTRO,
        messageText: message,
        violations: finalCheck.violations,
        severity: finalCheck.severity || "warn",
        wasBlocked: false,
        wasSent: true,
      }).catch(() => {});
    }

    const messageData = {
      text: message,
      chatId: chatId,
      createdAt: new Date().getTime(),
      senderId,
      receiverId,
    };

    try {
      const response = await addDirectMessage(messageData);
      if (response === 1) {
        sessionStorage.removeItem(`pendingMessage_${wantAd?.wantAdId}`);
        toast.success("Message sent successfully");
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("An error occurred while sending the message");
      console.error(error);
    } finally {
      setMessage("");
      setComplianceResult(null);
      lastCheckedRef.current = "";
      setIsLoading(false);
    }
  };

  const handleCheckboxClick = () => {
    setTermsAccepted(!termsAccepted);
  };

  return (
    <div className="ad-strengthen-wrap mb-0 mb-sm-4" data-tour="message-area">
      <div className="d-flex flex-column align-items-center">
        <div className="mb-2">
          <p className="f-medium f-size-18 send-message-listed-by">
            Listed By:
          </p>
          <p className="f-bold f-size-18 send-message-agent-username">
            {wantCreatedUserName}
          </p>
        </div>
        <img
          src={userAvatar || DefaultProfile}
          alt="avatar"
          className="property-agent-profile send-message-agent-profile mb-2"
          role="button"
          width="40"
          height="40"
          onClick={() => null}
        />
        <div className="d-flex flex-column align-items-center">
          <p className="send-message-company">{wantAdCompany}</p>
          <p className="send-message-license">{`Lic #${wantAdLicenseNo} `}</p>
          <a href={`tel:${wantAdPhone}`} className="send-message-phone">
            {wantAdPhone}
          </a>
          <a
            className="send-message-agent-bio"
            href={`/agent-info/${agentSlug}`}
          >
            {`View ${wantCreatedUserName}'s Full Bio`}{" "}
          </a>
        </div>
      </div>

      <div data-tour="clear-cooperation">
        <div className="addyour-property-details-wrap mb-18 property-message">
          <label className="form-label">Message</label>
          <TextArea
            name="message"
            id="message"
            className="form-control"
            errorMessage={msgError}
            value={message ? message : ""}
            maxLength={2000}
            placeholder={`Hi ${wantCreatedFirstName}, I have a few questions, when is a good time to connect?`}
            onChange={onChangeText}
            disabled={false}
          />
          <div className="des-count align-self-end">{messageCharCount}/2000</div>
          {complianceActive && complianceResult?.violations?.length > 0 && (
            <ComplianceWarning
              severity={complianceResult.severity}
              violations={complianceResult.violations}
            />
          )}
        </div>
        {isAgent && (
          <div className="mb-18 d-flex flex-row justify-content-center align-items-start">
            <Checkbox
              className="form-check-input"
              checked={termsAccepted}
              onChange={(e) => handleCheckboxClick(e)}
            />
            <span className="send-message-checkbox-label">
              I agree to adhere to the{" "}
              <a className="send-message-checkbox-link" href="">
                Clear Cooperation Policy
              </a>{" "}
              in all correspondences.
            </span>
          </div>
        )}
      </div>

      <Button
        style={{ letterSpacing: "unset !important" }}
        onClick={isLoggedIn ? handleSendMessage : handleSignUpRequired}
        disabled={isLoggedIn ? shouldDisableButton : messageCharCount <= 0}
      >
        Send Message
      </Button>
    </div>
  );
};

export default SendMessage;
